import { useState } from "react";
import { Gift, Search, CheckCircle, XCircle, AlertCircle, Copy, Check } from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition">
      {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
    </button>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    active:    { cls: "bg-green-100 text-green-800",  label: "Active"    },
    used:      { cls: "bg-gray-100 text-gray-600",    label: "Used"      },
    expired:   { cls: "bg-red-100 text-red-700",      label: "Expired"   },
    cancelled: { cls: "bg-orange-100 text-orange-700",label: "Cancelled" },
  };
  const s = map[status] || map.active;
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
};

const GiftCardsPage = () => {
  const [code, setCode] = useState("");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheck = async (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setLoading(true);
    setError("");
    setCard(null);
    try {
      const res = await axios.get(`${apiUrl}/giftcards/check/${trimmed}`);
      if (res.data?.status) {
        setCard(res.data.card);
      } else {
        setError(res.data?.error || "Gift card not found.");
      }
    } catch {
      setError("Unable to check this gift card. Please try again.");
    }
    setLoading(false);
  };

  const pct = card
    ? Math.round((Number(card.remaining_amount) / Number(card.initial_amount)) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-lg mx-auto space-y-8">

        {/* Header */}
        <div className="text-center">
          <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Gift size={28} className="text-orange-500" />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900">Gift Cards</h1>
          <p className="text-gray-500 mt-2">Check your gift card balance or redeem at checkout</p>
        </div>

        {/* Check form */}
        <div className="bg-white rounded-2xl border shadow-sm p-6 space-y-4">
          <p className="font-bold text-gray-800">Check Card Balance</p>
          <form onSubmit={handleCheck} className="flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              className="flex-1 border rounded-xl px-4 py-3 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl text-sm transition disabled:opacity-60 flex items-center gap-2"
            >
              <Search size={15} />
              {loading ? "..." : "Check"}
            </button>
          </form>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3">
              <XCircle size={16} className="flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Card result */}
        {card && (
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            {/* Card visual */}
            <div className={`relative p-6 text-white overflow-hidden select-none ${
              card.status === "active"
                ? "bg-gradient-to-br from-orange-500 via-orange-400 to-red-500"
                : "bg-gradient-to-br from-gray-400 to-gray-500"
            }`}>
              <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
              <div className="absolute -bottom-8 -left-4 w-36 h-36 bg-white/10 rounded-full" />
              <div className="relative z-10 flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-bold opacity-70 uppercase tracking-widest mb-1">Gamapa Gift Card</p>
                  <div className="flex items-center gap-1">
                    <p className="font-mono text-lg font-bold tracking-widest">{card.code}</p>
                    <CopyButton text={card.code} />
                  </div>
                </div>
                <StatusBadge status={card.status} />
              </div>
            </div>

            {/* Balance info */}
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Remaining Balance</p>
                  <p className="text-4xl font-extrabold text-orange-500 mt-1">
                    ${Number(card.remaining_amount).toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Original Value</p>
                  <p className="text-xl font-bold text-gray-700">${Number(card.initial_amount).toFixed(2)}</p>
                </div>
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{pct}% remaining</span>
                  <span>${(Number(card.initial_amount) - Number(card.remaining_amount)).toFixed(2)} used</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-400 to-red-400 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>

              {card.expires_at && (
                <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                  <AlertCircle size={15} className="flex-shrink-0 text-orange-400" />
                  Expires on {new Date(card.expires_at).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}
                </div>
              )}

              {card.status === "active" && Number(card.remaining_amount) > 0 && (
                <div className="flex items-start gap-3 bg-orange-50 rounded-xl p-4 text-sm text-orange-700">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Ready to use</p>
                    <p className="text-orange-600 mt-0.5">Enter your code <span className="font-mono font-bold">{card.code}</span> at checkout to apply your balance.</p>
                  </div>
                </div>
              )}

              {card.status !== "active" && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-xl p-4">
                  <XCircle size={16} className="flex-shrink-0" />
                  This gift card is <span className="font-bold capitalize ml-1">{card.status}</span> and cannot be used.
                </div>
              )}
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="bg-white rounded-2xl border p-6 space-y-3">
          <p className="font-bold text-gray-900">How to use a gift card</p>
          {[
            ["1", "Enter your 16-character gift card code above to check the balance"],
            ["2", "Add items to your cart and proceed to checkout"],
            ["3", "Enter your gift card code in the payment section"],
            ["4", "The balance will be applied to your order total"],
          ].map(([step, text]) => (
            <div key={step} className="flex items-start gap-3 text-sm text-gray-600">
              <span className="w-5 h-5 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{step}</span>
              {text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GiftCardsPage;
